import { useRef } from 'react';

import cs from 'classnames';

import { cn } from 'lib/helpers/utility';
import useAutosizeElement from 'lib/hooks/useAutosizeElement';

import Hint from '../shared/Hint';
import Header from '../shared/Header';
import TextEditor from '../TextEditor/TextEditor';
import MaximumChars from '../shared/MaximumChars';

import CopyButton from './components/CopyButton';
import getPaddingRight from './lib/getPaddingRight';
import ResizeGrabber from './components/ResizeGrabber';
import hasMeaningfulContent from './lib/hasMeaningfulContent';

/**
 * @param {import('./lib/propTypes').TextareaProps} props
 */

const Textarea = (props) => {
  const {
    label,
    placeholder,
    value,
    isEditor = false,
    id = label || 'textareaId',
    name = 'textareaName',
    className,
    width,
    maxHeight,
    disabled = false,
    error,
    hint,
    hintIcon = 'info-filled',
    forwardedRef,
    onChange = () => {},
    textareaClassName,
    maxLength,
    resizeY = false,
    bodyClassName,
    ...rest
  } = props;

  const internalRef = useRef(null);
  const textareaRef = forwardedRef || internalRef;
  const floorHeightRef = useRef(null);
  const isPinnedRef = useRef(null);

  const length = value?.length ?? 0;
  const isMaxChars = maxLength != null && length >= maxLength;

  useAutosizeElement({
    ref: textareaRef,
    value,
    isManual: resizeY,
    maxHeight,
    floorHeightRef,
    isPinnedRef
  });

  if (isEditor) return <TextEditor {...props} />;

  return (
    <div className={cn('relative', className)} style={{ width }}>
      <Header {...props} id={id} />
      <div
        className={cs(
          'flex flex-col overflow-hidden rounded-md border border-solid border-neutral-200 focus-within:outline focus-within:!outline-2',
          error || isMaxChars
            ? '!border-danger-300 caret-danger-500 focus-within:!outline-danger-100'
            : 'caret-primary-500 focus-within:!border-primary-300 focus-within:!outline-[#CBF0FD]',
          disabled
            ? 'bg-neutral-50 text-neutral-400 placeholder:text-neutral-300'
            : 'bg-white text-neutral-900 placeholder:text-neutral-500',
          bodyClassName
        )}>
        <div className="relative h-full">
          <textarea
            className={cn(
              'm-0 block h-10 w-full border-none py-[10px] pl-3 text-sm leading-5 outline-none',
              '!resize-none',
              maxHeight && `max-h-[${maxHeight}px]`,
              resizeY && 'min-h-[80px]',
              getPaddingRight({ maxLength, resizeY }),
              disabled && hasMeaningfulContent(value) && 'cursor-not-allowed pr-8',
              textareaClassName
            )}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            id={id}
            name={name}
            disabled={disabled}
            ref={textareaRef}
            maxLength={maxLength}
            {...rest}
          />
          <CopyButton value={value} disabled={disabled} />
          <MaximumChars
            length={length}
            maxLength={maxLength}
            isMaxChars={isMaxChars}
            disabled={disabled}
            isResize={resizeY}
          />
          <ResizeGrabber
            targetRef={textareaRef}
            disabled={disabled}
            isResize={resizeY}
            isMaxChars={isMaxChars}
            maxHeight={maxHeight}
            floorHeightRef={floorHeightRef}
            isPinnedRef={isPinnedRef}
          />
        </div>
      </div>
      <Hint hint={hint} icon={hintIcon} disabled={disabled} error={error} />
      {error && <p className="pt-2 text-sm text-danger-500">{error}</p>}
    </div>
  );
};

export default Textarea;
